export type AreaTreeBranch = {
    title: string;
    children: AreaTreeNode[];
    kind: "AreaTreeBranch";
};

export type AreaTreeLeaf = {
    title: string;
    description: string;
    ruleTableReference: RuleTableReference;
    sampleResult: RuleTableResult;
    context: RuleContext;
    kind: "AreaTreeLeaf";
};

export type ShowOrHideOptionResult = {
    shouldShowOption: boolean;
    kind: "ShowOrHideOptionResult";
};

export type ReplaceWithMessageResult = {
    message: string;
    url: string | null;
    type: string;
    kind: "ReplaceWithMessageResult";
};

export type ShowWithMessageResult = {
    message: string;
    url: string | null;
    type: string;
    kind: "ShowWithMessageResult";
};

export type IncludeOrExcludeResult = {
    shouldInclude: boolean;
    kind: "IncludeOrExcludeResult";
};

export type ClientDbmBookingWorkTypeContext = {
    vehicleBeingBooked: ClientVehicle;
    kind: "ClientDbmBookingWorkTypeContext";
};

export type DecryptedDbmBookingWorkTypeContext = {
    vehicleBeingBooked: DecryptedVehicle;
    kind: "DecryptedDbmBookingWorkTypeContext";
};

export type DbmBookingWorkTypeContext = {
    vehicleBeingBooked: Vehicle;
    kind: "DbmBookingWorkTypeContext";
};

export type ClientVehicle = {
    encryptedId: string;
};

export type DecryptedVehicle = {
    id: number;
};

export type Vehicle = {
    id: number;
    make: string;
    type: string;
    scheme: Scheme;
};

export type ClientScheme = {
    encryptedId: string;
    name: string;
};

export type Scheme = {
    id: number;
    name: string;
};

export type DbmBookingSiteSearchContext = {
    vehicleBeingBooked: Vehicle;
    site: Site;
    kind: "DbmBookingSiteSearchContext";
};

export type Site = {
    id: number;
    name: string;
    group: Group | null;
    hasMatchingFranchise: boolean;
    isIndependent: boolean;
    isMobile: boolean;
};

export type Group = {
    id: number;
    name: string;
};

export type DbmBookingWorkTypeRuleEvaluationRequest = {
    driverMicrositePin: string;
    ruleTableReference: RuleTableReference;
    contextualData: ClientDbmBookingWorkTypeContext;
};

export type MultipleDbmBookingWorkTypeRuleEvaluationRequest = {
    driverMicrositePin: string;
    ruleTableReferences: RuleTableReference[];
    contextualData: ClientDbmBookingWorkTypeContext;
};

export type PossibleValuesRequest = {
    ruleTableReference: RuleTableReference;
    attributePath: string;
    nameFilterPattern: string | null;
};

export type ListOfPresentableEntities = {
    entities: PresentableEntity[];
    kind: "ListOfPresentableEntities";
};

export type PresentableEntity = {
    id: number;
    name: string;
};

export type RuleTable = {
    headerCells: string[];
    rows: RuleTableRow[];
    defaultResult: RuleTableResult;
};

export type RuleTableRow = {
    oneBasedOrdinal: number;
    cells: ContextualDataPattern[];
    result: RuleTableResult;
};

export type BooleanPattern = {
    comparisonValue: boolean;
    kind: "BooleanPattern";
};

export type StringPattern = {
    patternOperator: StringToStringComparisonOperator;
    comparisonValue: string;
    kind: "StringPattern";
};

export type IdPattern = {
    patternOperator: IdToSetOfIdComparisonOperator;
    comparisonValue: number[];
    kind: "IdPattern";
};

export type AnyPattern = {
    kind: "AnyPattern";
};

export type OpeningTimesItem = {
    day: string;
    hours: string;
};

export type BookingDetails = {
    bookingId: string;
    date: Date | null;
    offeredDate: Date | null;
    firstPreferenceDate: Date | null;
    secondPreferenceDate: Date | null;
    thirdPreferenceDate: Date | null;
    whileUWait: boolean;
    collection: boolean;
    courtesyCar: boolean;
    collectionAddressLines: string[];
    bookingStatus: BookingStatus;
    bookingDeclinedReason: string;
    bookingContactPreference: BookingContactPreference;
    contactName: string;
    contactTelephoneNumber: string;
    contactEmail: string;
    collectionContactNumber: string;
    collectionContactName: string;
    collectionInstruction: string;
};

export type VehicleDetails = {
    encryptedId: string;
    scheme: ClientScheme;
    registrationNumber: string;
    make: string;
    model: string;
    derivative: string;
    odoReading: string;
    motDueDate: Date | null;
    lastOdoReading?: number | null;
    safetyRecall: boolean;
    assetId: string;
};

export type DriverDetails = {
    driverName: string;
    phoneNumber: string;
    smsMobileNo: string | null;
    email: string;
    contactPreferences: ContactPreferenceType[];
    bookerName: string;
    isBookingOnBehalf?: boolean
};

export type ServiceCentreDetails = {
    name: string;
    addressLines: string[];
    phoneNumber: string;
    openingTimes: OpeningTimesItem[];
    latitude: number;
    longitude: number;
};

export type BookingSummaryInformation = {
    bookingDetails: BookingDetails;
    vehicleDetails: VehicleDetails;
    serviceCentreDetails: ServiceCentreDetails;
    workDetails: WorkDetails;
    driverDetails: DriverDetails;
};

export type WorkDetails = {
    workDescriptions: string[];
    motRequired: boolean;
    tyreWorkDescription: string;
    wheelAlignmentRequired: boolean;
    userInvestigationLines: string[];
    additionalNotes: string | null;
};

export type NewBookingRequest = {
    selectedServiceCentreId: string;
    driverMicrositePin: string;
    vehicleRegNo: string;
    odoReading: number;
    driverName: string;
    email: string;
    phoneNumber: string;
    smsMobileNo: string | null;
    contactPreferences: ContactPreferenceType[];
    workRequired: WorkType[];
    repairOtherText: string | null;
    additionalServiceRequested: AdditionalServiceType;
    collectionAddress: string | null;
    requestedBookingDates: string[];
    previousBookingId: number | null;
    collectionContactName: string | null;
    collectionContactNumber: string | null;
    collectionInstructions: string | null;
    additionalNotes: string | null;
    ruleId: number | null;
    ruleParameterValueId: number[];
    ruleResultValueId: number[];
    isRequestDiaryIntegrated: boolean;
    bookerName: string;
    safetyRecall: boolean;
    temporaryBookingReference: string;
    referenceCode: string;
    assetId: string;
};

export type DriverMicrositeListingDetails = {
    driverMicrositeId: number;
    name: string;
    dateDeleted: Date | null;
    dateStatusLastModified: Date | null;
    driverMicrositeStatus: DriverMicrositeStatus;
};

export type MicrositeAuditDetails = {
    userName: string;
    modifiedDate: Date;
};

export type DriverMicrositeDetails = {
    driverMicrositeId: number;
    driverMicrositePin: string;
    driverMicrositeUrl: string;
    operatorId: number;
    name: string;
    title: string;
    customCompanyName: string;
    contactDetails: MicrositeContactDetails;
    schemes: SchemeDetails[];
    selectedSchemeIds: number[];
    serviceCentreType: ServiceCentreType;
    dateDeleted: Date | null;
    operatorPrefs: OperatorPref[];
    selectedOperatorPrefs: number[];
    driverMicrositeStatus: DriverMicrositeStatus;
    auditDetails: MicrositeAuditDetails;
    mailboxDelayMinutes: number;
    privacyPolicyUrl: string;
    driverMicrositeThemeId: number | null;
    driverMicrositeFooterLinks: DriverMicrositeFooterLink[];
};

export type OperatorPref = {
    operatorPrefId: number;
    prefText: string;
};

export type MicrositeContactDetails = {
    email: string;
    phoneNumber: string;
};

export type SchemeDetails = {
    schemeID: number;
    schemeName: string;
};

export type MicrositePageDetails = {
    micrositeContactDetails: MicrositeContactDetails;
    driverMicrositeStatus: DriverMicrositeStatus;
    micrositeSetting: MicrositeSetting;
};

export type MicrositeSetting = {
    customCompanyName: string;
    maxGarageSearchRadius: number;
    privacyPolicyUrl: string;
    title: string;
    showMotDueDate: boolean;
    calendarDaysBlockOutCollectionAndDelivery: number;
    calendarDaysBlockOutCourtesyCar: number;
    calendarDaysBlockOutDropOff: number;
    calendarDaysBlockOutWhileYouWait: number;
    showServiceCenterNameGarageSearchFilter: boolean;
    showSiteTypeGarageSearchFilter: boolean;
    showSearchRadiusGarageSearchFilter: boolean;
    showMainDealerGarageSearchFilter: boolean;
    displayNetworkPreferenceLevels: boolean;
    minimumNumberOfDiarySlotsAllowed: number;
    odoReadingCheck: boolean;
    serviceDeskBookerName: boolean;
    displayCollectionDistance: boolean;
    duplicateBookingCheck: boolean;
    hideSitesOutsideCollectionRadius: boolean;
    showHiddenSites: boolean;
    safetyRecallCheck: boolean;
    noBookingOptionsHelptext?: string;
    noBookingOptionsHelptextUrl?: string;
};

export type OperatorPrefsAndSchemes = {
    schemes: SchemeDetails[];
    operatorPrefs: OperatorPref[];
};

export type OperatorVehicleDetails = {
    encryptedId: string;
    scheme: ClientScheme;
    make: string;
    model: string;
    derivative: string;
    schemeBookingAcceptanceSms: boolean;
    motDueDate: Date | null;
    kind: "OperatorVehicleDetails";
    lastOdoReading: number | null;
    hasFutureOpenBooking?: boolean;
    assetId: string;
};

export type ServiceCentreCardDetails = {
    id: string;
    name: string;
    city: string;
    addressLines: string[];
    distance: number;
    openingTimes: OpeningTimesItem[];
    latitude: number;
    longitude: number;
    isHomeServiceCentre: boolean;
    isMobileServiceCentre: boolean;
    isMainDealer: boolean;
    odometerReading: number;
    ruleId: number | null;
    ruleParameterValueId: number[];
    ruleResultValueId: number[];
    preferredText: string | null;
    isDiaryIntegrated: boolean;
    collectionDeliveryRadius: number;
    distanceToCollectionDelivery: number;
    isCollectionDelivery: boolean;
    leadTime: number;
};

export type DriverMicrositeFormValues = {
    driverMicrositeId: number | null;
    operatorId: number;
    name: string;
    title: string;
    email: string;
    customCompanyName: string;
    phoneNumber: string;
    selectedSchemeIds: string[];
    serviceCentreType: ServiceCentreType;
    selectedOperatorPrefs: string[];
    mailboxDelayMinutes: number;
    privacyPolicyUrl: string;
    driverMicrositeThemeId: string | null;
    driverMicrositeFooterLinks: DriverMicrositeFooterLink[];
};

export type NotValidVehicle = {
    kind: "NotValidVehicle";
};

export type ServiceCentreSearchRequest = {
    driverMicrositePin: string;
    vehicleRegNo: string;
    odometerReading: string;
    postcodeOrTown: string;
    pageNo: number;
    resultsPerPage: number;
    searchRadiusMiles: number;
    requiredServices: ServiceType[];
    collectionPostCode?: string | null;
    hideSitesOutsideCollectionRadius: boolean;
    showHiddenSites: boolean;
    safetyRecall: boolean;
    searchAddress: string;
};

export type OperatorTyreServeDelegationScheme = {
    schemeId: number;
    schemeName: string;
    delegated: boolean;
};

export type TyreServeDelegationDetailsValues = {
    operatorId: number;
    schemeIds: number[];
};

export type DriverMicrositeThemeListingDetails = {
    driverMicrositeThemeId: number;
    themeName: string;
    isDefault: boolean;
};

export type DriverMicrositeThemeDetails = {
    driverMicrositeThemeId: number;
    themeName: string;
    operatorId: number;
    primaryColor: string;
    lightPrimaryColor: string;
    darkPrimaryColor: string;
    fonts: string;
    logoImage: string;
    favIcon: string;
    isDefault: boolean;
    footerImage: string;
    footerStraplineText: string;
};

export type DriverMicrositeThemeFormValues = {
    driverMicrositeThemeId: number | null;
    themeName: string;
    operatorId: number;
    primaryColor: string;
    lightPrimaryColor: string;
    darkPrimaryColor: string;
    fonts: string;
    logoImage: string;
    favIcon: string;
    isDefault: boolean;
    footerImage: string;
    footerStraplineText: string;
};

export type DriverMicrositeTheme = {
    primaryColor: string;
    lightPrimaryColor: string;
    darkPrimaryColor: string;
    fonts: string;
    logoImage: string;
    favIcon: string;
    footerImage: string;
    footerStraplineText: string;
};

export type DriverMicrositeFont = {
    driverMicrositeFontId: number;
    fontName: string;
    fontBlobGuid: string;
};

export type DriverMicrositeFooterLink = {
    ordinalNumber: number;
    linkText: string;
    linkUrl: string;
};

export type ServiceCentreLeadTimeRequest = {
    encryptedSiteId: string;
    driverMicrositePin: string;
    vehicleRegNo: string;
    collectionDeliveryRequested: boolean;
    courtesyCarRequested: boolean;
};

export type EncryptedEntityRequest = {
    encryptedEntityId: string;
};

export type RepairOperation = {
    code: string;
    text1?: string;
    text2?: string;
    text3?: string;
    text4?: string;
    text5?: string;
    text6?: string;
}

export type AvailabilityResponse = {
    leadTime: number;
    isFromDiaryIntegration: boolean;
    errorResponse: ErrorResponse;
    result: Availability[];
}

export type Availability = {
    availableDate: Date;
    availableTimes: AvailableTime[];
    colDelPostcodeAreas: string[];
    courtesyVehicle: boolean
}

export type AvailableTime = {
    time: number;
    whileYouWait: boolean;
    customSlot: boolean;
    motTime: number;
}

export type ErrorResponse = {
    errorCode: number;
    errorDetails: string;
}

export type CreateBookingResponse = {
    isFromDiaryIntegration: boolean;
    errorResponse: ErrorResponse;
    result: string;
}

export type CreateTemporaryBookingResponse = {
    temporaryBookingReference: string;
    errorResponse: ErrorResponse;
}

export type ClearTemporaryBookingRequest = {
    EncryptedSiteId: string,
    MicrositePin: string,
    VehicleReg: string,
    TemporaryBookingReference: string
}

export type ClearTemporaryBookingResponse = {
    isSuccessful: string;
    errorResponse: ErrorResponse;
}

export type OpeningTimesData = {
    mondayOpenTime: TimeOnly | null;
    mondayCloseTime: TimeOnly | null;
    tuesdayOpenTime: TimeOnly | null;
    tuesdayCloseTime: TimeOnly | null;
    wednesdayOpenTime: TimeOnly | null;
    wednesdayCloseTime: TimeOnly | null;
    thursdayOpenTime: TimeOnly | null;
    thursdayCloseTime: TimeOnly | null;
    fridayOpenTime: TimeOnly | null;
    fridayCloseTime: TimeOnly | null;
    saturdayOpenTime: TimeOnly | null;
    saturdayCloseTime: TimeOnly | null;
    sundayOpenTime: TimeOnly | null;
    sundayCloseTime: TimeOnly | null;
    closedDates: DateOnly[];
    saturdayHoursDates: DateOnly[];
    sundayHoursDates: DateOnly[];
};

export type DateOnly = {
    years: number;
    months: number;
    days: number;
};

export type TimeOnly = {
    hours: number;
    minutes: number;
};

export enum RuleTableReference {
    DbmBookingWorkTypeService = "DbmBookingWorkTypeService",
    DbmBookingWorkTypeTyres = "DbmBookingWorkTypeTyres",
    DbmBookingWorkTypeMot = "DbmBookingWorkTypeMot",
    DbmBookingWorkTypeRepair = "DbmBookingWorkTypeRepair",
    DbmBookingBookingTypeDeliveryAndCollection = "DbmBookingBookingTypeDeliveryAndCollection",
    DbmBookingBookingTypeCourtesyCar = "DbmBookingBookingTypeCourtesyCar",
    DbmBookingBookingTypeWhileUWait = "DbmBookingBookingTypeWhileUWait",
    DbmBookingBookingTypeDropOffVehicle = "DbmBookingBookingTypeDropOffVehicle",
    DbmBookingSiteFiltering = "DbmBookingSiteFiltering",
}

export enum StringToStringComparisonOperator {
    Equals = "Equals",
    Contains = "Contains",
}

export enum IdToSetOfIdComparisonOperator {
    OneOf = "OneOf",
}

export enum ContactPreferenceType {
    Sms = "Sms",
    Email = "Email",
}

export enum BookingContactPreference {
    Email = "Email",
    Telephone = "Telephone",
}

export enum BookingStatus {
    Received = "Received",
    AlternativeDateOffered = "AlternativeDateOffered",
    Confirmed = "Confirmed",
    Declined = "Declined",
    Cancelled = "Cancelled",
    Expired = "Expired",
}

export enum WorkType {
    Service = "Service",
    Tyres = "Tyres",
    Mot = "Mot",
    RepairOther = "RepairOther",
}

export enum DriverMicrositeStatus {
    Suspended = "Suspended",
    Approved = "Approved",
    Cancelled = "Cancelled",
    DisabledForOperator = "DisabledForOperator",
}

export enum AdditionalServiceType {
    NoAdditional = "NoAdditional",
    Delivery = "Delivery",
    Courtesy = "Courtesy",
    WhileUWait = "WhileUWait"
}

export enum ServiceType {
    None = "None",
    ServiceRepair = "ServiceRepair",
    Tyres = "Tyres",
    Mot = "Mot",
    CollectionDelivery = "CollectionDelivery",
    CourtesyCar = "CourtesyCar",
    Callout24Hour = "Callout24Hour",
    BreakdownCover = "BreakdownCover",
    Inspections = "Inspections",
    ElectricHybrid = "ElectricHybrid",
    HeavyVan = "HeavyVan",
    Service = "Service",
    Repair = "Repair"
}

export enum ServiceCentreType {
    Franchise = "Franchise",
    NonFranchise = "NonFranchise",
    Both = "Both",
}

export enum SiteTypeFilter {
    Fixed = "Fixed",
    Mobile = "Mobile",
    All = "All",
}

export type AreaTreeNode = AreaTreeBranch | AreaTreeLeaf

export type RuleTableResult = ShowHideOrMessageResult | IncludeOrExcludeResult

export type ShowHideOrMessageResult = ShowOrHideOptionResult | ReplaceWithMessageResult | ShowWithMessageResult

export type ClientRuleContext = ClientDbmBookingWorkTypeContext

export type DecryptedRuleContext = DecryptedDbmBookingWorkTypeContext

export type RuleContext = DbmBookingWorkTypeContext | DbmBookingSiteSearchContext

export type PossibleValuesResponse = ListOfPresentableEntities

export type ContextualDataPattern = BooleanPattern | StringPattern | IdPattern | AnyPattern

export type OperatorVehicleResult = OperatorVehicleDetails | NotValidVehicle